import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import SEOHead from '../../components/SEOHead';
import { useNavigate } from 'react-router-dom';
import ProfessionalLayout from '../../components/ProfessionalLayout';
import { Card, CardContent } from '../../components/ui/card';
import { auditActionLabel } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Package,
  Users,
  MapPin,
  Plane,
  Plus,
  ArrowRight,
  Clock,
  CheckCircle,
  Search,
  Camera,
  AlertTriangle,
  TrendingUp,
  BarChart3,
  DollarSign,
  ShieldAlert,
  PackageCheck,
  Wallet,
  FileText
} from 'lucide-react';
import '../../styles/professional-theme.css';
import LoaderDashboard from './LoaderDashboard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (date, t, language = 'fr') => {
  const now = new Date();
  const past = new Date(date);
  const diffMs = now - past;
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return t('justNow');
  if (diffMin < 60) return t('minutesAgo').replace('{n}', diffMin);
  if (diffH < 24) return t('hoursAgo').replace('{n}', diffH);
  if (diffD < 7) return t('daysAgo').replace('{n}', diffD);
  return past.toLocaleDateString(language === 'en' ? 'en-GB' : 'fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getActivityIcon = (action) => {
  switch (action) {
    case 'PACKAGE_CREATED': return Package;
    case 'PACKAGE_STATUS_UPDATED': return CheckCircle;
    case 'SHIPMENT_CREATED': return Plane;
    case 'FILE_UPLOADED': return Camera;
    case 'USER_CREATED': return Users;
    default: return Clock;
  }
};

const UnifiedDashboard = () => {
  const { user } = useAuth();
  // Rôle de supervision cross-société, rattaché à aucune entreprise :
  // aucune action d'exploitation (colis, départ) ne lui est proposée.
  const isPlatformAdmin = user?.role === 'platform_admin';
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [operationalIndicators, setOperationalIndicators] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    if (user?.role === 'local_partner') {
      navigate('/local-deliveries', { replace: true });
      return;
    }
    if (user?.role === 'partner') {
      navigate('/my-packages', { replace: true });
      return;
    }
    fetchOperationalIndicators();
    fetchRecentActivity();
  }, [user?.role]);

  const fetchOperationalIndicators = async () => {
    try {
      const response = await api.get(`${API}/analytics/operational-indicators`);
      setOperationalIndicators(response.data);
    } catch (err) {
      console.error('Error fetching indicators:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await api.get(`${API}/audit-logs?limit=8`);
      setRecentActivity(response.data);
    } catch {
      // empty state handles it
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/my-packages?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  // Le chargeur a sa propre vue (chargement / vérification) — pas la vue d'ensemble admin.
  if (user?.role === 'loader') {
    return <LoaderDashboard />;
  }

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="analytics-loading">
          <div className="analytics-skeleton" style={{ height: 28, width: 220, marginBottom: 8 }} />
          <div className="analytics-skeleton" style={{ height: 18, width: 340, marginBottom: 32 }} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="analytics-skeleton" style={{ height: 110, borderRadius: 10 }} />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="analytics-skeleton lg:col-span-1" style={{ height: 300, borderRadius: 10 }} />
            <div className="analytics-skeleton lg:col-span-2" style={{ height: 300, borderRadius: 10 }} />
          </div>
        </div>
      </ProfessionalLayout>
    );
  }

  const pendingTotal = operationalIndicators?.pending_packages?.total || 0;
  const pendingWeight = operationalIndicators?.pending_packages?.total_weight || 0;
  const partnersCount = operationalIndicators?.partners_involved?.count || 0;
  const destinations = operationalIndicators?.top_destinations || [];
  const recommendation = operationalIndicators?.recommendation || {};
  const awaitingCustoms = operationalIndicators?.awaiting_customs?.count || 0;
  const staleCount = operationalIndicators?.stale_packages?.count || 0;
  const deliveredCount = operationalIndicators?.delivered_packages?.count || 0;
  const overdueInvoices = operationalIndicators?.overdue_invoices?.count || 0;
  const openIncidents = operationalIndicators?.open_incidents?.count || 0;
  const pendingCollections = operationalIndicators?.pending_collections?.total || 0;
  const pendingCollaborators = operationalIndicators?.pending_collections?.collaborators || 0;
  const pendingExpenses = operationalIndicators?.pending_expenses?.count || 0;

  const urgencyDot = recommendation.urgency === 'high' ? '#dc2626'
    : recommendation.urgency === 'medium' ? '#f59e0b' : '#16a34a';

  const urgencyLabel = recommendation.urgency === 'high' ? 'URGENT'
    : recommendation.urgency === 'medium' ? 'MODERE' : 'STABLE';

  const warningItems = [
    { key: 'delivered', count: deliveredCount, label: t('deliveredPackages'), icon: PackageCheck, color: 'text-green-600', bg: 'bg-green-500/10', path: '/shipments?status=delivered' },
    { key: 'overdue', count: overdueInvoices, label: t('overdueInvoices7d'), icon: DollarSign, color: 'text-red-600', bg: 'bg-red-500/10', path: '/finance/invoices?status=pending' },
    { key: 'customs', count: awaitingCustoms, label: t('awaitingCustomsClearance'), icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-500/10', path: '/shipments?status=awaiting_customs' },
    { key: 'incidents', count: openIncidents, label: t('reportedIncidents'), icon: ShieldAlert, color: 'text-orange-600', bg: 'bg-orange-500/10', path: '/incidents?status=open' },
    { key: 'stale', count: staleCount, label: t('noUpdate3d'), icon: Clock, color: 'text-red-500', bg: 'bg-red-500/10', path: '/shipments?status=stale' },
    { key: 'collections', count: pendingCollections, label: `${pendingCollections.toLocaleString('fr-FR')} € ${language === 'en' ? `held by ${pendingCollaborators} collaborator(s)` : `chez ${pendingCollaborators} collaborateur(s)`}`, icon: Wallet, color: 'text-orange-600', bg: 'bg-orange-500/10', path: '/finance' },
    { key: 'expenses', count: pendingExpenses, label: language === 'en' ? `expense report(s) pending review` : `note(s) de frais en attente`, icon: FileText, color: 'text-purple-600', bg: 'bg-purple-500/10', path: '/finance' },
  ];
  const activeWarnings = warningItems.filter(w => w.count > 0);

  return (
    <ProfessionalLayout>
      <SEOHead title={t('seoDashboardTitle')} description={t('seoDashboardDesc')} noindex />
      {/* Header */}
      <div className="analytics-header">
        <span className="analytics-label">{t('dashboard')}</span>
        <h1 className="analytics-title">
          {t('hello')}, <strong>{user?.name || user?.email}</strong>
        </h1>
        <div className="analytics-header-row">
          <span className="analytics-update">
            <Clock size={14} />
            {t('updatedJustNow')}
          </span>
          {/* Search */}
          <form onSubmit={handleSearch} className="relative w-full sm:w-auto">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('searchPackage')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 w-full sm:w-[280px] pl-9 text-sm border border-border rounded-lg"
            />
          </form>
        </div>
      </div>

      {/* Warning Alerts — clickable, apply filter directly */}
      {activeWarnings.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
          {activeWarnings.map(w => {
            const Icon = w.icon;
            return (
              <div
                key={w.key}
                onClick={() => navigate(w.path)}
                className={`flex items-center gap-3 p-3 rounded-lg border border-border ${w.bg} cursor-pointer hover:opacity-80 transition-opacity`}
              >
                <Icon size={18} className={w.color} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground">{w.count}</div>
                  <div className="text-xs text-muted-foreground truncate">{w.label}</div>
                </div>
                <ArrowRight size={14} className="text-muted-foreground" />
              </div>
            );
          })}
        </div>
      )}

      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="analytics-kpi" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #e8ecf4 100%)', borderColor: '#c7d2e8' }}>
          <div className="analytics-kpi-label">
            <div className="analytics-kpi-dot" style={{ background: '#3b82f6' }} />
            {t('pendingPackages')}
          </div>
          <div className="analytics-kpi-value">{pendingTotal}</div>
          <div className="analytics-kpi-delta">{pendingWeight} kg {t('total').toLowerCase()}</div>
        </div>

        <div className="analytics-kpi" style={{ background: 'linear-gradient(135deg, #fff4ed 0%, #ffe8d6 100%)', borderColor: '#f5c9a8' }}>
          <div className="analytics-kpi-label">
            <div className="analytics-kpi-dot" style={{ background: '#e8590c' }} />
            {t('activePartners')}
          </div>
          <div className="analytics-kpi-value">{partnersCount}</div>
          <div className="analytics-kpi-delta">{t('withPendingPackages')}</div>
        </div>

        <div className="analytics-kpi" style={{ background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', borderColor: '#c4b5fd' }}>
          <div className="analytics-kpi-label">
            <div className="analytics-kpi-dot" style={{ background: '#8b5cf6' }} />
            {t('destinations')}
          </div>
          <div className="analytics-kpi-value">{destinations.length}</div>
          <div className="analytics-kpi-delta">{t('destinationCountries')}</div>
        </div>

        <div className="analytics-kpi" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', borderColor: '#86efac' }}>
          <div className="analytics-kpi-label">
            <div className="analytics-kpi-dot" style={{ background: urgencyDot }} />
            {t('recommendation')}
          </div>
          <div className="analytics-kpi-value" style={{ fontSize: 20, fontWeight: 700 }}>
            {urgencyLabel}
          </div>
          <div className="analytics-kpi-delta">{recommendation.message || '—'}</div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Left Column — Quick Actions + Destinations */}
        <div className="flex flex-col gap-6">
          {/* Quick Actions — réservées aux rôles rattachés à une entreprise.
              Un platform_admin (company_id null) ne crée ni colis ni départ. */}
          {!isPlatformAdmin && (
          <Card className="analytics-card">
            <CardContent className="p-5">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-4">
                {t('quickActions')}
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  size="sm"
                  className="h-10 justify-between text-sm"
                  onClick={() => navigate('/my-packages')}
                >
                  <span className="flex items-center gap-2">
                    <Plus size={16} />
                    {t('createPackage')}
                  </span>
                  <ArrowRight size={16} />
                </Button>

                <Button
                  variant="secondary"
                  size="sm"
                  className="h-10 justify-between text-sm"
                  onClick={() => {
                    if (user?.role === 'partner' || user?.role === 'local_partner') {
                      return;
                    }
                    navigate('/shipments');
                  }}
                >
                  <span className="flex items-center gap-2">
                    <Plane size={16} />
                    {t('newShipment')}
                  </span>
                  <ArrowRight size={16} />
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 justify-between text-sm"
                  onClick={() => navigate('/analytics')}
                >
                  <span className="flex items-center gap-2">
                    <BarChart3 size={16} />
                    {t('viewAnalytics')}
                  </span>
                  <ArrowRight size={16} />
                </Button>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Top Destinations */}
          {destinations.length > 0 && (
            <Card className="analytics-card">
              <CardContent className="p-5">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-4">
                  {t('topDestinations')}
                </div>
                <div className="overflow-x-auto">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>{t('country')}</th>
                        <th style={{ textAlign: 'right' }}>{t('packages')}</th>
                        <th style={{ textAlign: 'right' }}>{t('weight')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {destinations.slice(0, 5).map((dest, i) => (
                        <tr key={i}>
                          <td className="font-medium">{dest.country || dest._id || '—'}</td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {dest.count || 0}
                          </td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {(dest.total_weight || 0).toFixed(1)} kg
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Status Summary — now rendered as top-level warning grid above */}
        </div>

        {/* Right Column — Recent Activity */}
        <div className="lg:col-span-2">
          <Card className="analytics-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-5">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {t('recentActivity')}
                </div>
                <button
                  onClick={() => navigate('/activity-log')}
                  className="text-xs font-medium text-accent hover:underline"
                >
                  {t('viewAll')}
                </button>
              </div>

              {recentActivity.length === 0 ? (
                <div className="analytics-empty">
                  {t('noRecentActivity')}
                </div>
              ) : (
                <div className="flex flex-col">
                  {recentActivity.map((activity, idx) => {
                    const Icon = getActivityIcon(activity.action);
                    const label = auditActionLabel(activity.action, language);
                    return (
                      <div
                        key={activity.id}
                        className={`flex items-center gap-3 py-3 ${
                          idx < recentActivity.length - 1 ? 'border-b border-border' : ''
                        }`}
                      >
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <Icon size={15} className="text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="mb-0.5">
                            <span className="text-sm font-medium text-foreground truncate block">
                              {label}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {activity.user_email} &middot; {timeAgo(activity.created_at, t, language)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </ProfessionalLayout>
  );
};

export default UnifiedDashboard;
